import React, { useEffect, useRef, useState } from "react";
import axios, { AxiosResponse } from "axios";
import { DataTableWithoutButtons } from "components/DataTable/DataTableWithoutButtons";
import { AfDataType } from "utils/TableColumnData";
import handleWheel from "utils/handleOnWheel";
import { ListCommanWay, UpdateCommanWay } from "API/commonApi";
import { UseMutateFunction, useMutation, useQuery } from "@tanstack/react-query";
import { Img } from "components";
import moment from "moment";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import Select from "react-select";
import { roles } from "utils/constant";
import { utils, write, writeFile } from "xlsx";
import { saveAs } from "file-saver";
import Spinner from "utils/Spinner";
import {
  ArrowDownIcon,
  ArrowsUpDownIcon,
  ArrowUpIcon,
} from "@heroicons/react/24/outline";

interface PaymentOutstandingProps {
  startDate?: string;
  endDate?: string;
  searchIncrement?: number;
  tableRef?: any;
}

const PaymentOutstanding: React.FC<PaymentOutstandingProps> = (props) => {
  const ADT = AfDataType;
  const tableRef = useRef<any>(null);

  const [tableData, setTableData] = useState<any>([]);
  const [tableDetails, setTableDetails] = useState<any>([]);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" | null }>({
    key: "status",
    direction: "asc",
  });
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");

  const [startDate, setStartDate] = useState<any>(props.startDate || moment().startOf("day").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState<any>(props.endDate || moment().endOf("day").format("YYYY-MM-DD"));
  const [query, setQuery] = useState<any>({
    fromDate: props.startDate ? moment(props.startDate).startOf("day") : moment().startOf("day"),
    toDate: props.endDate ? moment(props.endDate).endOf("day") : moment().endOf("day"),
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [fetchedData, setFetchedData] = useState([]);
  const [isDataAdded, setIsDataAdded] = useState(false);

  const [selectedAgent, setSelectedAgent] = useState({ id: undefined, name: "--- Select User ---" });
  const [allAgents, setAllAgents] = useState([]);

  useEffect(() => {
    const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
    if (Object.keys(userD).length) {
      setUserDetails(userD.state.user);
      if (userD.state.user.roleName == roles.SUPER_ADMIN  || userD.state.user.roleName == roles.CA_READ_ONLY) {
        mutateListAgents({ subbranch: undefined });
      } else if (userD.state.user.roleName == roles.SUBBRANCH_ADMIN || userD.state.user.roleName == roles.CUSTOMER_SERVICE) {
        mutateListAgents({ subbranch: userD.state.user?.subbranch });
      } else {
        mutateListAgents({ subbranch: undefined });
      }
    }
  }, []);
  const { mutate: mutateListAgents }: any = useMutation(
    (qry: any) => ListCommanWay("/admin/user/list", { subbranch: qry?.subbranch, isActive: true }, { paginate: 100000 }),
    {
      onSuccess: (data: any) => {
        if (data.status == "SUCCESS") {
          setAllAgents([{ id: undefined, name: "--- Select User ---" }, ...data.data.data]);
        }
      },
      onError: (data: any) => {},
    }
  );

  useEffect(() => {
    if (props.startDate || props.endDate) {
      setStartDate(props.startDate || moment().startOf("day").format("YYYY-MM-DD"));
      setEndDate(props.endDate || moment().endOf("day").format("YYYY-MM-DD"));
      setQuery((prev: any) => ({
        ...prev,
        fromDate: props.startDate ? moment(props.startDate).startOf("day") : moment().startOf("day"),
        toDate: props.endDate ? moment(props.endDate).endOf("day") : moment().endOf("day"),
      }));
    }
  }, [props.startDate, props.endDate, props.searchIncrement]);

  const handleSubmit = async () => {
    setIsDataAdded(!isDataAdded);
    setQuery({
      fromDate: startDate ? moment(startDate).startOf("day") : moment().startOf("day"),
      toDate: endDate ? moment(endDate).endOf("day") : moment().endOf("day"),
      userId: userDetails.roleName == "DELIVERY" ? userDetails.id : selectedAgent.id ? selectedAgent.id : undefined,
    });
  };

  const fetchAllCnDn = async () => {
    let allNotes: any[] = [];
    try {
      const res = await ListCommanWay(
        "/admin/cn-dn/list",
        {},
        { page: 1, paginate: 10000, include: ["_customerId", "_addedBy", "_subbranch"] }
      );
      if (res?.status === "SUCCESS") {
        const pageData = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
          ? res.data
          : [];
        allNotes.push(...pageData);

        const pageCount = res.data?.paginator?.pageCount || 1;
        if (pageCount > 1) {
          for (let p = 2; p <= Math.min(pageCount, 20); p++) {
            try {
              const nextRes = await ListCommanWay(
                "/admin/cn-dn/list",
                {},
                { page: p, paginate: 10000, include: ["_customerId", "_addedBy", "_subbranch"] }
              );
              if (nextRes?.status === "SUCCESS") {
                const nextData = Array.isArray(nextRes.data?.data)
                  ? nextRes.data.data
                  : Array.isArray(nextRes.data)
                  ? nextRes.data
                  : [];
                allNotes.push(...nextData);
              }
            } catch (pErr) {
              console.error("Error fetching CN/DN page " + p, pErr);
            }
          }
        }
      }
    } catch (err) {
      console.error("Error fetching CN/DN in Outstanding Report:", err);
    }
    return allNotes;
  };

  const fetchCustomers = async () => {
    let allCusts: any[] = [];
    try {
      const res = await ListCommanWay("/admin/customer/list", {}, { paginate: 10000 });
      if (res?.status === "SUCCESS") {
        allCusts = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
      }
    } catch (err) {
      console.error("Error fetching customers in Outstanding Report:", err);
    }
    return allCusts;
  };

  const { isFetching, isLoading, refetch } = useQuery(
    ["admin_outstanding_report", query, props.searchIncrement],
    async () => {
      const [reportRes, notes, customers] = await Promise.all([
        ListCommanWay("/admin/outstanding/report", query, {
          include: [],
          paginate: 10000,
          doSum: true,
        }),
        fetchAllCnDn(),
        fetchCustomers(),
      ]);

      if (reportRes?.status !== "SUCCESS") {
        return [];
      }

      const rawData = Array.isArray(reportRes.data)
        ? reportRes.data
        : (Array.isArray(reportRes.data?.data) ? reportRes.data.data : []);

      // Build customer dictionaries
      const customerById: Record<string, any> = {};
      const customerByCode: Record<string, any> = {};
      const customerByName: Record<string, any> = {};

      customers.forEach((c: any) => {
        if (c.id != null) customerById[String(c.id)] = c;
        if (c.code) customerByCode[String(c.code).trim().toLowerCase()] = c;
        if (c.sh) customerByName[String(c.sh).trim().toLowerCase()] = c;
        if (c.name) customerByName[String(c.name).trim().toLowerCase()] = c;
      });

      // Filter active and valid IRN CN/DN notes (ACTIVE, NA, N/A)
      const isValidIrnStatus = (status: any) => {
        const s = String(status || "").trim().toUpperCase();
        return s === "ACTIVE" || s === "NA" || s === "N/A";
      };

      // Aggregates for CN and DN per customer
      const cnMapById: Record<string, { totalCn: number; totalDn: number }> = {};
      const cnMapByCode: Record<string, { totalCn: number; totalDn: number }> = {};
      const cnMapByName: Record<string, { totalCn: number; totalDn: number }> = {};

      notes.forEach((note: any) => {
        if (!isValidIrnStatus(note.irn_status)) return;

        const noteVal = Number(note.total_value || 0);
        if (!noteVal) return;

        const isCn = note.note_type === "CN";
        const isDn = note.note_type === "DN";

        const rawId = note.customerId || note._customerId?.id;
        const rawCode = note._customerId?.code ? String(note._customerId.code).trim().toLowerCase() : "";
        const cust = (rawId && customerById[String(rawId)]) || (rawCode && customerByCode[rawCode]);

        const resolvedId = rawId ? String(rawId) : (cust?.id ? String(cust.id) : "");
        const resolvedCode = rawCode || (cust?.code ? String(cust.code).trim().toLowerCase() : "");
        const resolvedName = cust?.sh ? String(cust.sh).trim().toLowerCase() : (cust?.name ? String(cust.name).trim().toLowerCase() : "");

        if (resolvedId) {
          if (!cnMapById[resolvedId]) cnMapById[resolvedId] = { totalCn: 0, totalDn: 0 };
          if (isCn) cnMapById[resolvedId].totalCn += noteVal;
          if (isDn) cnMapById[resolvedId].totalDn += noteVal;
        }
        if (resolvedCode) {
          if (!cnMapByCode[resolvedCode]) cnMapByCode[resolvedCode] = { totalCn: 0, totalDn: 0 };
          if (isCn) cnMapByCode[resolvedCode].totalCn += noteVal;
          if (isDn) cnMapByCode[resolvedCode].totalDn += noteVal;
        }
        if (resolvedName) {
          if (!cnMapByName[resolvedName]) cnMapByName[resolvedName] = { totalCn: 0, totalDn: 0 };
          if (isCn) cnMapByName[resolvedName].totalCn += noteVal;
          if (isDn) cnMapByName[resolvedName].totalDn += noteVal;
        }
      });

      // Process report rows
      const processedData = rawData.map((row: any) => {
        const rowId = row.customer_id || row.customerId || row.id;
        const rowCode = row.customer_code ? String(row.customer_code).trim().toLowerCase() : "";
        const rowName = row.customer_name ? String(row.customer_name).trim().toLowerCase() : "";

        const cust = (rowId && customerById[String(rowId)]) ||
                     (rowCode && customerByCode[rowCode]) ||
                     (rowName && customerByName[rowName]);

        const targetId = rowId ? String(rowId) : (cust?.id ? String(cust.id) : "");
        const targetCode = rowCode || (cust?.code ? String(cust.code).trim().toLowerCase() : "");
        const targetName = rowName || (cust?.sh ? String(cust.sh).trim().toLowerCase() : (cust?.name ? String(cust.name).trim().toLowerCase() : ""));

        const cnData = (targetId && cnMapById[targetId]) ||
                       (targetCode && cnMapByCode[targetCode]) ||
                       (targetName && cnMapByName[targetName]) ||
                       { totalCn: 0, totalDn: 0 };

        const dnAmount = Number((cnData.totalDn || 0).toFixed(2));
        const cnAmount = Number((cnData.totalCn || 0).toFixed(2));

        const origBilled = Number(row.total_billed || 0);
        const origReceived = Number(row.total_received_net || 0);
        const origEffRcvd = Number(row.total_effective_received || 0);

        // Include Debit Note in Total Invoice
        const totalBilled = Number((origBilled + dnAmount).toFixed(2));
        // Include Credit Note in Total Received and Effective Received
        const totalReceivedNet = Number((origReceived + cnAmount).toFixed(2));
        const totalEffRcvd = Number((origEffRcvd + cnAmount).toFixed(2));

        // Outstanding amount = Total Invoice (including DN) - Effective Received (including CN)
        const adjustedOutstanding = Number((totalBilled - totalEffRcvd).toFixed(2));

        let status = row.status;
        if (status !== "No Invoice") {
          status = adjustedOutstanding < 10 ? "Cleared" : "Pending";
        }

        // Wallet customer calculation: customer.negative_balance == 0 means wallet customer, otherwise non-wallet
        const negativeBalance = row.negative_balance != null
          ? row.negative_balance
          : (cust?.negative_balance != null ? cust.negative_balance : 1);
        const isWallet = Number(negativeBalance) === 0 || negativeBalance === false;
        const wallet = isWallet ? "Yes" : "No";

        // Active customer calculation: customer.isActive == 0 means inactive (red dot), otherwise active (green dot)
        const rawActive = row.isActive != null
          ? row.isActive
          : (row.is_active != null ? row.is_active : (cust?.isActive != null ? cust.isActive : 1));
        const isCustActive = rawActive !== 0 && rawActive !== false && rawActive !== "0";

        return {
          ...row,
          dn_amount: dnAmount,
          cn_amount: cnAmount,
          orig_billed: origBilled,
          orig_received_net: origReceived,
          orig_eff_rcvd: origEffRcvd,
          total_billed: totalBilled,
          total_received_net: totalReceivedNet,
          total_effective_received: totalEffRcvd,
          outstanding_amount: adjustedOutstanding,
          status,
          wallet,
          isWallet,
          isActive: isCustActive,
        };
      });

      return processedData;
    },
    {
      keepPreviousData: true,
      onSuccess(processed) {
        const sortedData = sortData(processed, "customer_name", "asc");
        const filteredData = sortedData.filter((item: any) => item.status !== "No Invoice");
        setTableData(filteredData);
        setTableDetails([]);
      },
      onError() {
        setTableData([]);
      },
    }
  );

  useEffect(() => {
    if (query.userId) {
      refetch();
    }
  }, [query]);

  const BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
  const [userDetails, setUserDetails] = useState<any>(userD?.state?.user || {});

  const sortData = (data: any[], key: string, direction: "asc" | "desc" | null) => {
    if (!direction) return data;

    return [...data].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      // Special handling for status to put "No Invoice" at bottom
      if (key === "status") {
        if (aVal === "No Invoice") return 1;
        if (bVal === "No Invoice") return -1;
      }

      // Handle numeric columns
      if (["total_billed", "total_received_net", "total_effective_received", "outstanding_amount"].includes(key)) {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }

      // Handle date column
      if (key === "last_payment_date") {
        aVal = aVal ? moment(aVal).valueOf() : 0;
        bVal = bVal ? moment(bVal).valueOf() : 0;
      }

      // Handle string comparison
      if (typeof aVal === "string" && typeof bVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return direction === "asc" ? -1 : 1;
      if (aVal > bVal) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (key: string) => {
    let direction: "asc" | "desc" | null = "asc";

    if (sortConfig.key === key) {
      if (sortConfig.direction === "asc") {
        direction = "desc";
      } else if (sortConfig.direction === "desc") {
        direction = null;
      }
    }

    setSortConfig({ key, direction });

    if (direction) {
      const sorted = sortData(tableData, key, direction);
      setTableData(sorted);
    } else {
      // Reset to original order with status sort
      const sorted = sortData(tableData, "status", "asc");
      setTableData(sorted);
      setSortConfig({ key: "status", direction: "asc" });
    }
  };

  const getSortIcon = (columnKey: string) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowsUpDownIcon className="w-3.5 h-3.5 inline text-slate-400 ml-1" />;
    }
    if (sortConfig.direction === "asc") {
      return <ArrowUpIcon className="w-3.5 h-3.5 inline text-blue-600 font-bold ml-1" />;
    }
    if (sortConfig.direction === "desc") {
      return <ArrowDownIcon className="w-3.5 h-3.5 inline text-blue-600 font-bold ml-1" />;
    }
    return <ArrowsUpDownIcon className="w-3.5 h-3.5 inline text-slate-400 ml-1" />;
  };

  const displayedData = React.useMemo(() => {
    if (statusFilter === "active") {
      return tableData.filter((item: any) => item.isActive === true);
    }
    if (statusFilter === "inactive") {
      return tableData.filter((item: any) => item.isActive === false);
    }
    return tableData;
  }, [tableData, statusFilter]);

  const activeCount = React.useMemo(() => tableData.filter((c: any) => c.isActive).length, [tableData]);
  const inactiveCount = React.useMemo(() => tableData.filter((c: any) => !c.isActive).length, [tableData]);

  const calculateTotals = () => {
    return displayedData.reduce(
      (acc: any, row: any) => {
        acc.total_billed += Number(row.total_billed || 0);
        acc.total_received_net += Number(row.total_received_net || 0);
        acc.total_effective_received += Number(row.total_effective_received || 0);
        acc.outstanding_amount += Number(row.outstanding_amount || 0);
        if (row.wallet === "Yes") acc.walletCount += 1;
        if (row.status === "Cleared") acc.clearedCount += 1;
        else acc.pendingCount += 1;
        return acc;
      },
      {
        total_billed: 0,
        total_received_net: 0,
        total_effective_received: 0,
        outstanding_amount: 0,
        walletCount: 0,
        clearedCount: 0,
        pendingCount: 0,
      }
    );
  };

  const totals = calculateTotals();

  const exportToExcel = () => {
    const table = tableRef.current;
    if (!table) return;

    const tableClone = (table as HTMLElement).cloneNode(true) as HTMLElement;

    const tfoot = tableClone.getElementsByTagName("tfoot");
    if (tfoot.length > 0) {
      tfoot[0].remove();
    }

    const ws = utils.table_to_sheet(tableClone);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Outstanding Report");

    Object.keys(ws).forEach((key) => {
      if (key.startsWith("!")) return;

      let cell = ws[key];
      if (cell && cell.h && typeof cell.h === "string") {
        cell.h = null;
      }
      if (cell && cell.l) {
        delete cell.l;
      }
    });

    const wbout = write(wb, { bookType: "xlsx", type: "array" });

    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "Customer Outstanding Report.xlsx");
  };

  return (
    <>
      {(isLoading || isFetching) && <Spinner />}

      {/* Customer Status Filter & Quick Metric Badges */}
      <div className="flex flex-wrap items-center justify-between gap-2.5 px-1 mb-2.5">
        <div className="flex flex-wrap items-center gap-2">
          {/* Segmented Filter Control */}
          <div className="inline-flex items-center p-0.5 bg-slate-200/80 rounded-xl border border-slate-300 shadow-2xs">
            <button
              type="button"
              onClick={() => setStatusFilter("all")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                statusFilter === "all"
                  ? "bg-white text-blue-700 shadow-xs border border-slate-200"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              All Customers{" "}
              <span
                className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  statusFilter === "all" ? "bg-blue-100 text-blue-800" : "bg-slate-300 text-slate-700"
                }`}
              >
                {tableData.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setStatusFilter("active")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                statusFilter === "active"
                  ? "bg-white text-emerald-700 shadow-xs border border-slate-200"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              Active{" "}
              <span
                className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  statusFilter === "active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-300 text-slate-700"
                }`}
              >
                {activeCount}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setStatusFilter("inactive")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                statusFilter === "inactive"
                  ? "bg-white text-rose-700 shadow-xs border border-slate-200"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-rose-500" />
              Inactive{" "}
              <span
                className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  statusFilter === "inactive" ? "bg-rose-100 text-rose-800" : "bg-slate-300 text-slate-700"
                }`}
              >
                {inactiveCount}
              </span>
            </button>
          </div>

          {/* Quick Counts */}
          <span className="bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-lg text-xs font-semibold border border-emerald-200">
            {totals.clearedCount} Cleared
          </span>
          <span className="bg-amber-50 text-amber-700 px-2.5 py-1 rounded-lg text-xs font-semibold border border-amber-200">
            {totals.pendingCount} Pending
          </span>
          <span className="bg-purple-50 text-purple-700 px-2.5 py-1 rounded-lg text-xs font-semibold border border-purple-200">
            {totals.walletCount} Wallet (Prepaid)
          </span>
        </div>

        {/* Status Dots Legend */}
        <div className="flex items-center gap-3 text-[11px] text-slate-600 bg-white/80 px-3 py-1 rounded-lg border border-slate-200/70 shadow-xs">
          <span className="inline-flex items-center gap-1.5 font-medium">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> Active Customer
          </span>
          <span className="inline-flex items-center gap-1.5 font-medium">
            <span className="w-2 h-2 rounded-full bg-rose-500 inline-block" /> Inactive Customer
          </span>
        </div>
      </div>

      {/* Results Table Container */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-200/80 overflow-hidden">
        <div className="overflow-x-auto">
          {!isLoading && !isFetching && (!displayedData || displayedData.length === 0) && (
            <div className="flex flex-col items-center py-16">
              <svg className="w-16 h-16 text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-slate-600 font-semibold text-sm">
                {statusFilter === "active"
                  ? "No active customer records found"
                  : statusFilter === "inactive"
                  ? "No inactive customer records found"
                  : "No customer records found"}
              </p>
              <p className="text-slate-400 text-xs mt-0.5">Try adjusting your filters</p>
            </div>
          )}

          {displayedData && displayedData.length > 0 && (
            <table
              className="w-full border-collapse"
              id="myTable"
              style={{ fontSize: "85%" }}
              ref={(el) => {
                tableRef.current = el;
                if (props.tableRef) props.tableRef.current = el;
              }}
            >
              <thead className="sticky top-0 z-10 bg-slate-100">
                {/* Summary Totals Row */}
                <tr className="bg-slate-100 font-bold text-slate-800 border-b border-slate-200">
                  <td className="border border-slate-200 px-2 py-2 text-center" colSpan={4}>
                    TOTAL
                  </td>
                  <td className="border border-slate-200 px-2 py-2 text-right font-mono">{totals.total_billed.toLocaleString("en-IN")}</td>
                  <td className="border border-slate-200 px-2 py-2 text-right font-mono text-emerald-700">{totals.total_received_net.toLocaleString("en-IN")}</td>
                  <td className="border border-slate-200 px-2 py-2 text-right font-mono text-indigo-700">{totals.total_effective_received.toLocaleString("en-IN")}</td>
                  <td className="border border-slate-200 px-2 py-2 text-right font-mono font-bold text-rose-700">{totals.outstanding_amount.toLocaleString("en-IN")}</td>
                  <td className="border border-slate-200 px-2 py-2 text-center" colSpan={2}></td>
                </tr>
                {/* Column Headers */}
                <tr className="bg-slate-200 text-slate-700 font-semibold text-[11px] uppercase tracking-wider">
                  <th className="border border-slate-200 px-2 py-2 text-center w-10">Sr.</th>
                  <th className="border border-slate-200 px-2 py-2 text-center cursor-pointer hover:bg-slate-300 transition-colors" onClick={() => handleSort("customer_code")}>
                    Customer Code {getSortIcon("customer_code")}
                  </th>
                  <th className="border border-slate-200 px-2 py-2 text-left cursor-pointer hover:bg-slate-300 transition-colors" onClick={() => handleSort("customer_name")}>
                    Customer Name {getSortIcon("customer_name")}
                  </th>
                  <th className="border border-slate-200 px-2 py-2 text-center cursor-pointer hover:bg-slate-300 transition-colors" onClick={() => handleSort("wallet")}>
                    Wallet {getSortIcon("wallet")}
                  </th>
                  <th className="border border-slate-200 px-2 py-2 text-right cursor-pointer hover:bg-slate-300 transition-colors" onClick={() => handleSort("total_billed")}>
                    Total Invoice {getSortIcon("total_billed")}
                  </th>
                  <th className="border border-slate-200 px-2 py-2 text-right cursor-pointer hover:bg-slate-300 transition-colors" onClick={() => handleSort("total_received_net")}>
                    Total Received {getSortIcon("total_received_net")}
                  </th>
                  <th className="border border-slate-200 px-2 py-2 text-right cursor-pointer hover:bg-slate-300 transition-colors" onClick={() => handleSort("total_effective_received")}>
                    Effective Received {getSortIcon("total_effective_received")}
                  </th>
                  <th className="border border-slate-200 px-2 py-2 text-right cursor-pointer hover:bg-slate-300 transition-colors" onClick={() => handleSort("outstanding_amount")}>
                    Outstanding Amt {getSortIcon("outstanding_amount")}
                  </th>
                  <th className="border border-slate-200 px-2 py-2 text-center cursor-pointer hover:bg-slate-300 transition-colors" onClick={() => handleSort("last_payment_date")}>
                    Last Pay Date {getSortIcon("last_payment_date")}
                  </th>
                  <th className="border border-slate-200 px-2 py-2 text-center cursor-pointer hover:bg-slate-300 transition-colors" onClick={() => handleSort("status")}>
                    Status {getSortIcon("status")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayedData.map((r: any, i: number) => {
                  return (
                    <tr
                      key={"outstand-" + i}
                      className={`border-b border-slate-200 transition-colors ${
                        r.status === "Cleared"
                          ? "bg-emerald-50/40 hover:bg-emerald-100/60"
                          : "hover:bg-slate-50"
                      }`}
                    >
                      <td className="border border-slate-200 px-2 py-1.5 text-center text-slate-500">{i + 1}</td>
                      <td className="border border-slate-200 px-2 py-1.5 text-center">
                        <div className="inline-flex items-center gap-1.5 font-mono font-medium text-slate-800">
                          <span
                            className={`w-2 h-2 rounded-full flex-shrink-0 ${r.isActive ? "bg-emerald-500" : "bg-rose-500"}`}
                            title={r.isActive ? "Active Customer" : "Inactive Customer"}
                          />
                          <span>{r.customer_code}</span>
                        </div>
                      </td>
                      <td className="border border-slate-200 px-2 py-1.5 font-medium text-slate-800 font-RobotoCondensed">
                        {r.customer_name}
                      </td>
                      <td className="border border-slate-200 px-2 py-1.5 text-center">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${
                            r.wallet === "Yes"
                              ? "bg-purple-100 text-purple-700 border border-purple-200"
                              : "bg-slate-100 text-slate-600 border border-slate-200"
                          }`}
                        >
                          {r.wallet}
                        </span>
                      </td>
                      <td
                        className="border border-slate-200 px-2 py-1.5 text-right font-mono text-slate-700"
                        title={r.dn_amount ? `Invoices: ₹${Number(r.orig_billed || 0).toLocaleString("en-IN")} + DN: ₹${Number(r.dn_amount).toLocaleString("en-IN")}` : undefined}
                      >
                        {Number(r.total_billed || 0).toLocaleString("en-IN")}
                      </td>
                      <td
                        className="border border-slate-200 px-2 py-1.5 text-right font-mono text-emerald-700"
                        title={r.cn_amount ? `Received: ₹${Number(r.orig_received_net || 0).toLocaleString("en-IN")} + CN: ₹${Number(r.cn_amount).toLocaleString("en-IN")}` : undefined}
                      >
                        {Number(r.total_received_net || 0).toLocaleString("en-IN")}
                      </td>
                      <td
                        className="border border-slate-200 px-2 py-1.5 text-right font-mono font-medium text-slate-800"
                        title={r.cn_amount ? `Effective: ₹${Number(r.orig_eff_rcvd || 0).toLocaleString("en-IN")} + CN: ₹${Number(r.cn_amount).toLocaleString("en-IN")}` : undefined}
                      >
                        {Number(r.total_effective_received || 0).toLocaleString("en-IN")}
                      </td>
                      <td className="border border-slate-200 px-2 py-1.5 text-right font-mono font-bold text-slate-900">
                        {Number(r.outstanding_amount || 0).toLocaleString("en-IN")}
                      </td>
                      <td className="border border-slate-200 px-2 py-1.5 text-center whitespace-nowrap text-slate-600">
                        {r.last_payment_date ? moment(r.last_payment_date).format("DD-MM-YYYY") : "-"}
                      </td>
                      <td className="border border-slate-200 px-2 py-1.5 text-center">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${
                            r.status === "Cleared"
                              ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
                              : "bg-amber-100 text-amber-700 border border-amber-300"
                          }`}
                        >
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 font-bold text-slate-800 border-t-2 border-slate-300">
                  <td className="border border-slate-200 px-2 py-2 text-center" colSpan={4}>
                    TOTAL
                  </td>
                  <td className="border border-slate-200 px-2 py-2 text-right font-mono">{totals.total_billed.toLocaleString("en-IN")}</td>
                  <td className="border border-slate-200 px-2 py-2 text-right font-mono text-emerald-700">{totals.total_received_net.toLocaleString("en-IN")}</td>
                  <td className="border border-slate-200 px-2 py-2 text-right font-mono text-indigo-700">{totals.total_effective_received.toLocaleString("en-IN")}</td>
                  <td className="border border-slate-200 px-2 py-2 text-right font-mono font-bold text-rose-700">{totals.outstanding_amount.toLocaleString("en-IN")}</td>
                  <td className="border border-slate-200 px-2 py-2 text-center" colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </>
  );
};

export default PaymentOutstanding;
